/**
 * The composer's pill row (Figma 505:50225 "Macros", under the editor): the
 * Go actions (Confirm rate, Final confirmation) and the agent's suggested
 * replies, one horizontal row scrolled by touch or trackpad. Presentational
 * only; the composer decides what a tap does.
 */
import {
  Fragment,
  useCallback,
  useEffect,
  useId,
  useRef,
  useState,
  type KeyboardEvent,
} from 'react';
import { Skeleton } from '../../../components/ui/skeleton';
import { cn } from '../../../lib/utils';

export interface ComposerPill {
  id: string;
  label: string;
  /** Why the pill cannot be used right now; it stays focusable and says so. */
  blockedReason?: string;
}

export interface ReplySuggestionStripProps {
  /** The reply's suggestions are still being read: skeletons stand in for them. */
  pending: boolean;
  pills: ComposerPill[];
  /** A send or a draft is in flight: a tap does nothing. */
  disabled?: boolean;
  onSelect: (id: string) => void;
}

const FADE =
  'linear-gradient(to right, transparent 0, #000 1.5rem, #000 calc(100% - 1.5rem), transparent 100%)';
const FADE_START = 'linear-gradient(to right, transparent 0, #000 1.5rem)';
const FADE_END = 'linear-gradient(to right, #000 calc(100% - 1.5rem), transparent 100%)';

function prefersReducedMotion(): boolean {
  return (
    typeof window !== 'undefined' &&
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches
  );
}

export function ReplySuggestionStrip({
  pending,
  pills,
  disabled,
  onSelect,
}: ReplySuggestionStripProps) {
  const uid = useId();
  const barRef = useRef<HTMLDivElement>(null);
  const pillRefs = useRef<(HTMLButtonElement | null)[]>([]);
  const [active, setActive] = useState(0);
  const [edges, setEdges] = useState({ start: false, end: false });

  const measure = useCallback(() => {
    const bar = barRef.current;
    if (!bar) return;
    const next = {
      start: bar.scrollLeft > 1,
      end: bar.scrollLeft + bar.clientWidth < bar.scrollWidth - 1,
    };
    setEdges((current) =>
      current.start === next.start && current.end === next.end ? current : next,
    );
  }, []);

  useEffect(() => {
    measure();
    const bar = barRef.current;
    if (!bar || typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(measure);
    observer.observe(bar);
    return () => observer.disconnect();
  }, [measure, pending, pills.length]);

  if (!pending && pills.length === 0) return null;

  const current = Math.max(0, Math.min(active, pills.length - 1));
  const move = (to: number) => {
    const next = Math.max(0, Math.min(pills.length - 1, to));
    setActive(next);
    pillRefs.current[next]?.focus();
    pillRefs.current[next]?.scrollIntoView?.({
      inline: 'nearest',
      block: 'nearest',
      behavior: prefersReducedMotion() ? 'auto' : 'smooth',
    });
  };
  const onKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    const keys: Record<string, number> = {
      ArrowRight: current + 1,
      ArrowLeft: current - 1,
      Home: 0,
      End: pills.length - 1,
    };
    const to = keys[event.key];
    if (to === undefined || pills.length === 0) return;
    event.preventDefault();
    move(to);
  };
  const mask =
    edges.start && edges.end ? FADE : edges.start ? FADE_START : edges.end ? FADE_END : undefined;

  return (
    <div
      ref={barRef}
      role="toolbar"
      aria-label="Suggested replies"
      aria-orientation="horizontal"
      onKeyDown={onKeyDown}
      onScroll={measure}
      style={mask ? { maskImage: mask, WebkitMaskImage: mask } : undefined}
      className="flex touch-pan-x snap-x snap-proximity scrollbar-none items-center gap-2 overflow-x-auto overscroll-x-contain px-3 pb-2 [&::-webkit-scrollbar]:hidden"
    >
      {pills.map((pill, i) => {
        const inert = disabled || !!pill.blockedReason;
        const reasonId = pill.blockedReason ? `${uid}-reason-${i}` : undefined;
        return (
          <Fragment key={pill.id}>
            <button
              ref={(el) => {
                pillRefs.current[i] = el;
              }}
              type="button"
              tabIndex={i === current ? 0 : -1}
              aria-disabled={inert ? true : undefined}
              aria-describedby={reasonId}
              title={pill.blockedReason ?? pill.label}
              onFocus={() => setActive(i)}
              onClick={() => {
                if (!inert) onSelect(pill.id);
              }}
              className={cn(
                'border-border inline-flex h-7 max-w-[min(20rem,85%)] shrink-0 snap-start items-center gap-1 rounded-lg border bg-(--background-primary-light) px-2 py-1 text-sm/5 whitespace-nowrap text-(--foreground-secondary-default)',
                'hover:bg-accent focus-visible:outline-ring focus-visible:outline-2 focus-visible:outline-offset-2',
                inert && 'cursor-not-allowed opacity-60',
              )}
            >
              <span className="truncate">{pill.label}</span>
            </button>
            {pill.blockedReason && (
              <span id={reasonId} className="sr-only">
                {pill.blockedReason}
              </span>
            )}
          </Fragment>
        );
      })}
      {pending && (
        <span role="status" aria-busy="true" className="flex shrink-0 gap-2">
          <span className="sr-only">Reading the reply…</span>
          <Skeleton className="h-7 w-28 rounded-lg" />
          <Skeleton className="h-7 w-36 rounded-lg" />
        </span>
      )}
    </div>
  );
}
